// services/AlertService.js
// Sistema alert ottimizzato:
// - Meteoalarm per mappe ampie (Italia/Regioni)
// - WeatherAPI/OpenWeather per percorsi specifici

const CACHE_DURATION = 30 * 60 * 1000; // 30 minuti

// Cache separata per tipo
let meteoalarmCache = null;
let meteoalarmCacheTime = null;

let routeAlertsCache = new Map(); // Cache per coordinate specifiche

// ============================================
// CONFIGURAZIONE
// ============================================
const API_BASE_URL = process.env.REACT_APP_API_URL_NODE ||
    (typeof window !== 'undefined' && window.location.hostname === 'aihiking.eu'
        ? 'https://aitrek-server-node.onrender.com'
        : 'http://localhost:3004');
const API_SOURCES = {
    meteoalarm: {
        url: `${API_BASE_URL}/api/meteoalarm`,
        enabled: true
    }
};

// Coordinate regioni
const REGION_COORDS = {
    'abruzzo': [42.3498, 13.3995],
    'basilicata': [40.6389, 15.8056],
    'calabria': [38.9100, 16.5877],
    'campania': [40.8518, 14.2681],
    'emilia-romagna': [44.4949, 11.3426],
    'friuli-venezia giulia': [45.6495, 13.7768],
    'lazio': [41.9028, 12.4964],
    'liguria': [44.4056, 8.9463],
    'lombardia': [45.4642, 9.1900],
    'marche': [43.6158, 13.5189],
    'molise': [41.6759, 14.6099],
    'piemonte': [45.0703, 7.6869],
    'puglia': [41.1171, 16.8719],
    'sardegna': [40.1209, 9.0129],
    'sicilia': [37.5999, 14.0153],
    'toscana': [43.7711, 11.2486],
    'trentino-alto adige': [46.0664, 11.1257],
    'umbria': [43.1122, 12.3888],
    'valle d\'aosta': [45.7383, 7.3206],
    'veneto': [45.4408, 12.3155]
};

// ============================================
// HELPER FUNCTIONS
// ============================================

function mapSeverity(source, value) {
    const val = (value || '').toLowerCase();

    if (val.includes('extreme') || val.includes('red')) return 'extreme';
    if (val.includes('severe') || val.includes('high') || val.includes('orange')) return 'high';
    if (val.includes('moderate') || val.includes('medium') || val.includes('yellow')) return 'medium';
    return 'low';
}

function mapAlertType(event) {
    const eventLower = (event || '').toLowerCase();

    if (eventLower.includes('thunder') || eventLower.includes('storm')) return 'weather';
    if (eventLower.includes('snow') || eventLower.includes('neve')) return 'snow';
    if (eventLower.includes('wind') || eventLower.includes('vento')) return 'wind';
    if (eventLower.includes('rain') || eventLower.includes('flood') || eventLower.includes('alluvione')) return 'flood';
    if (eventLower.includes('avalanche') || eventLower.includes('valanga')) return 'avalanche';

    return 'weather';
}

function normalizeRegionName(name) {
    return name.toLowerCase()
        .replace(/[àá]/g, 'a')
        .replace(/[èé]/g, 'e')
        .replace(/[ìí]/g, 'i')
        .replace(/[òó]/g, 'o')
        .replace(/[ùú]/g, 'u')
        .trim();
}

// ============================================
// METEOALARM (Per Mappe Ampie)
// ============================================

async function fetchMeteoalarmAlerts() {
    try {
        console.log('🇪🇺 Meteoalarm: Recupero alert Italia...');

        const response = await fetch(API_SOURCES.meteoalarm.url);

        if (!response.ok) {
            console.error('❌ Meteoalarm error:', response.status);
            return [];
        }

        const xmlText = await response.text();
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xmlText, 'text/xml');

        const parserError = xmlDoc.querySelector('parsererror');
        if (parserError) {
            console.error('❌ XML parsing error');
            return [];
        }

        const entries = xmlDoc.querySelectorAll('entry');

        if (entries.length === 0) {
            console.log('ℹ️ Meteoalarm: Nessun entry nel feed');
            return [];
        }

        console.log(`📊 Meteoalarm: ${entries.length} entry trovate`);

        const alerts = [];
        const now = new Date();
        let skippedExpired = 0;

        entries.forEach((entry, index) => {
            try {
                const title = entry.querySelector('title')?.textContent || '';
                const summary = entry.querySelector('summary')?.textContent || '';

                if (!title) return;

                // Trova data scadenza
                const findDate = (tags) => {
                    for (const tag of tags) {
                        let el = entry.querySelector(tag);
                        if (el?.textContent) return el.textContent;

                        el = entry.querySelector(`cap\\:${tag}`);
                        if (el?.textContent) return el.textContent;

                        el = entry.querySelector(`[*|${tag}]`);
                        if (el?.textContent) return el.textContent;
                    }
                    return null;
                };

                const expiresText = findDate(['expires', 'expiry']);
                const effectiveText = findDate(['effective', 'onset']);
                const updatedText = entry.querySelector('updated')?.textContent;

                let validUntil = null;

                if (expiresText) {
                    validUntil = new Date(expiresText);
                } else if (effectiveText) {
                    validUntil = new Date(new Date(effectiveText).getTime() + 24 * 3600000);
                } else if (updatedText) {
                    validUntil = new Date(new Date(updatedText).getTime() + 24 * 3600000);
                }

                if (!validUntil || isNaN(validUntil.getTime())) {
                    validUntil = new Date(now.getTime() + 24 * 3600000);
                }

                // Filtra scaduti
                if (validUntil < now) {
                    skippedExpired++;
                    return;
                }

                // Parse titolo: "Yellow Wind Warning issued for Italy - Sicilia"
                let regionName = 'italia';
                if (title.includes(' - ')) {
                    const parts = title.split(' - ');
                    regionName = normalizeRegionName(parts[parts.length - 1].trim());
                }

                // Severity da colore
                let severity = 'medium';
                const titleLower = title.toLowerCase();
                if (titleLower.startsWith('red')) severity = 'extreme';
                else if (titleLower.startsWith('orange')) severity = 'high';
                else if (titleLower.startsWith('yellow')) severity = 'medium';
                else if (titleLower.startsWith('green')) severity = 'low';

                // Tipo evento
                const eventMatch = title.match(/^(Red|Orange|Yellow|Green)\s+(\w+)\s+Warning/i);
                const eventType = eventMatch?.[2] ? mapAlertType(eventMatch[2]) : 'weather';

                // Coordinate
                const coords = REGION_COORDS[regionName] || [42.5, 12.5];

                // Messaggio pulito
                const cleanMessage = title.replace(/\s+issued for Italy\s*-?\s*/i, ' - ');

                // Area formattata
                const areaFormatted = regionName
                    .split('-')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ')
                    .replace("D'", "d'");

                alerts.push({
                    id: `meteoalarm_${index}_${now.getTime()}`,
                    type: eventType,
                    severity: severity,
                    coords: coords,
                    area: areaFormatted,
                    message: cleanMessage,
                    details: summary || 'Consultare bollettino ufficiale',
                    validUntil: validUntil,
                    source: 'Meteoalarm EU (Ufficiale)'
                });

            } catch (err) {
                console.warn('⚠️ Errore parsing entry:', err);
            }
        });

        console.log(`✅ Meteoalarm: ${alerts.length} alert validi (${skippedExpired} scaduti)`);

        return alerts;

    } catch (error) {
        console.error('❌ Errore Meteoalarm:', error);
        return [];
    }
}

// ============================================
// FETCH ALERT PER MAPPE AMPIE (Solo Meteoalarm)
// ============================================

export async function fetchAlerts(bounds, forceFresh = false) {
    const now = Date.now();

    // Cache
    if (!forceFresh && meteoalarmCache && meteoalarmCacheTime &&
        (now - meteoalarmCacheTime) < CACHE_DURATION) {
        console.log('📦 Cache alert:', meteoalarmCache.length);
        return meteoalarmCache;
    }

    console.log('🌐 Fetching alert Italia (Meteoalarm)...');

    try {
        // Solo Meteoalarm per mappe ampie
        const alerts = await fetchMeteoalarmAlerts();

        if (alerts.length === 0) {
            console.log('✅ Nessun alert meteo attivo in Italia');
        }

        // Filtra per bounds visibili
        const filteredAlerts = alerts.filter(alert => {
            const [lat, lon] = alert.coords;
            return lat >= bounds.south && lat <= bounds.north &&
                lon >= bounds.west && lon <= bounds.east;
        });

        // Cache
        meteoalarmCache = filteredAlerts;
        meteoalarmCacheTime = now;

        console.log(`📊 Alert finali: ${filteredAlerts.length}`);

        return filteredAlerts;

    } catch (error) {
        console.error('❌ Error fetching alerts:', error);

        if (meteoalarmCache) {
            return meteoalarmCache;
        }

        return [];
    }
}


// ============================================
// UTILITY
// ============================================

export function invalidateCache() {
    meteoalarmCache = null;
    meteoalarmCacheTime = null;
    routeAlertsCache.clear();
    console.log('🗑️ Cache invalidata');
}

export function filterAlertsByBounds(alerts, bounds) {
    return alerts.filter(alert => {
        const [lat, lon] = alert.coords;
        return lat >= bounds.south && lat <= bounds.north &&
            lon >= bounds.west && lon <= bounds.east;
    });
}

export default {
    fetchAlerts,
    invalidateCache,
    filterAlertsByBounds
};